import React, { useEffect, useRef, useState } from "react";
import DashboardContent from "../../components/DashboardContent";
import useModalWindow from "../../hooks/useModalWindow";
import useCsr from "../../hooks/useCsr";
import { useNavigate, useParams } from "react-router-dom";
import { CsrAPI } from "../../apis/CsrAPI";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";

const LaySummary = () => {
  const { studyId } = useParams();
  const { modalWindow } = useModalWindow();
  const { setAjaxLoading, currentStudyName, wrongEnvironment } = useCsr();
  const navigate = useNavigate();

  const studyNameRef = useRef();
  const errRef = useRef();

  const [studyName, setStudyName] = useState("");
  const studyType = "CSR";
  const [errMsg, setErrMsg] = useState("");

  const [showRedirect, setShowRedirect] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (currentStudyName) {
      setStudyName(currentStudyName);
    }
  }, [currentStudyName]);

  useEffect(() => {
    if (!errMsg) {
      studyNameRef.current?.focus();
    }
  }, [errMsg]);

  useEffect(() => {
    setErrMsg("");
  }, [studyId]);

  const handleStudyNameChange = event => {
    setStudyName(event.target.value);
  };

  useEffect(() => {
    setErrMsg("");
  }, [studyName]);

  const handleSummary = async event => {
    event.preventDefault();

    try {
      const isConfirmed = await modalWindow({
        title: "Note to the writer",
        message:
          "Note to the writer: You will be asked several questions about the study design and table formats. Please be prepared by having available a copy of the CSP, SAP and TFLs. In particular, you will be asked for the name of treatment groups, populations, variables. etc., used in the TFL column headers or table body (not in the footnote) or in the TFL titles - you must use the exact wording and spelling (i.e., PIO, PLO, or Placebo; ITT or Intent-to-Treat).",
        confirmButtonLabel: "OK",
      });

      if (isConfirmed) {
        setAjaxLoading(true);

        try {
          const response = await CsrAPI.createStudy({ studyName, studyType });
          const newStudyId = response?.study_id;
          setErrMsg("");

          if (mountedRef.current) {
            if (mountedRef.current) {
              navigate("/ls/" + newStudyId + "/lay-summary-questions");
            }
          }
        } catch (error) {
          if (mountedRef.current) {
            if (isProduction) {
              Sentry.captureException(error);
            }
            if (!error?.response) {
              setErrMsg("No Server Response");
            } else if (error.response?.status === 401) {
              setErrMsg("Invalid user credentials");
            } else if (error.response?.status === 403) {
              setShowRedirect(true);
            } else {
              setErrMsg("Fail to create a new study");
            }
            errRef.current?.focus();
          }
        } finally {
          if (mountedRef.current) {
            setAjaxLoading(false);
          }
        }
      }
    } catch (modalError) {
      if (mountedRef.current) {
        setErrMsg("An unexpected error occurred");
        errRef.current?.focus();
      }
    }
  };

  const PageContent = () => {
    return (
      <div className={"csr-summary csr-summary__wrapper"}>
        <form id={"ls-summary-form"} onSubmit={handleSummary}>
          <span
            ref={errRef}
            className={errMsg ? "errmsg" : "offscreen"}
            aria-live="assertive">
            {errMsg}
          </span>
          <div className={"form-component csr-summary__form-component"}>
            <label htmlFor="studyname">Lay Summary Name</label>
            {currentStudyName ? (
              <input
                type="text"
                id="studyname"
                ref={studyNameRef}
                autoComplete="off"
                value={currentStudyName}
                required
                aria-invalid={currentStudyName ? "false" : "true"}
                placeholder={"Study name"}
                className={"input-text"}
                disabled={true}
              />
            ) : (
              <input
                type="text"
                id="studyname"
                ref={studyNameRef}
                autoComplete="off"
                onChange={handleStudyNameChange}
                value={studyName}
                required
                aria-invalid={studyName ? "false" : "true"}
                placeholder={"Study name"}
                className={"input-text"}
              />
            )}
          </div>
        </form>
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Lay Summary Report Name"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Select Study",
        buttonGoToAction: "/ls/" + studyId + "/select-study",
      }}
      nextButton={{
        buttonTitle: "Wizard Questions",
        buttonFormAction: studyId ? "" : "ls-summary-form",
        buttonGoToAction: studyId
          ? "/ls/" + studyId + "/lay-summary-questions"
          : "",
      }}
    />
  );
};

export default LaySummary;
